import React from "react";
import { useApp } from "@/state/AppContext";

export function cityInitial(name) {
  const chars = Array.from((name || "B").trim());
  return (chars[0] || "B").toUpperCase();
}

export function CityLogo({ name, size = 36, className = "" }) {
  const letter = cityInitial(name);
  return (
    <svg
      data-testid="city-logo"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      role="img"
      aria-label={name || "City"}
      className={`shrink-0 ${className}`}
    >
      <circle cx="32" cy="32" r="32" fill="hsl(var(--brand-deep))" />
      <text
        x="27"
        y="46"
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontWeight="700"
        fontSize="42"
        fill="var(--brand-cream, #f7f3ec)"
      >
        {letter}
      </text>
      <circle cx="45.5" cy="43" r="9.5" fill="hsl(var(--accent))" />
    </svg>
  );
}

/**
 * The subtitle text: "{City} Online" in English; in Hindi "{name_hi} ऑनलाइन" when the tenant has a
 * Hindi name, otherwise it falls back to the English "{City} Online". The only place the string is
 * built, so the lockup, the landing-page city chips and the admin network view cannot drift.
 */
export function useCityOnline() {
  const { t, lang } = useApp();
  return (name, nameHi) =>
    lang === "hi" && nameHi ? t("brand.cityOnline", { city: nameHi }) : t("brand.cityOnlineLatin", { city: name });
}

/**
 * The one brand lockup. "Apna Shahar" is the prominent mark (heading font, bold, sized by the
 * caller's `className`, 16px in the citizen header and 18px in the console header/footer);
 * directly under it sits the "{City} Online" subtitle: Title Case (never uppercase), muted,
 * always 12px (`text-xs`, one step under the smallest mark), no letter-spacing. Nothing else may
 * re-build this line. `withTagline` exists for layout flexibility, but every mount leaves it on.
 * `onDark` recolours both lines for the dark landing-page surfaces. `subtitle` replaces the city line
 * with a role label ("Platform admin") for the one console that has no city; same style.
 */
export function CityWordmark({ name, nameHi, subtitle, className = "", withTagline = true, onDark = false }) {
  const cityOnline = useCityOnline();
  const { t } = useApp();
  return (
    <span data-testid="city-wordmark" className="inline-flex min-w-0 flex-col leading-tight">
      {/* whitespace-nowrap on both lines: the mark may truncate with an ellipsis in a tight
          container, but it must never wrap, squeeze or letter-collide. */}
      <span
        className={`block truncate whitespace-nowrap font-heading font-bold tracking-tight ${
          onDark ? "text-[var(--as-on-dark)]" : "text-foreground"
        } ${className}`}
      >
        {t("brand.platform")}
      </span>
      {withTagline && (
        <span
          data-testid="city-wordmark-endorsement"
          className={`block truncate whitespace-nowrap text-xs font-medium ${
            onDark ? "text-[var(--as-on-dark-3)]" : "text-muted-foreground"
          }`}
        >
          {subtitle ?? cityOnline(name, nameHi)}
        </span>
      )}
    </span>
  );
}
